import { useEffect, useRef } from "react";
import {
  HEATMAP_GRADIENT,
  HEATMAP_RADIUS_PX,
} from "./heatmapUtils";

const HEATMAP_RENDER_SCALE = 0.5;
const DRAW_DEBOUNCE_MS = 80;

let gradientLookupCache = null;

const createGradientLookup = () => {
  if (gradientLookupCache) return gradientLookupCache;

  const lookupCanvas = document.createElement("canvas");
  lookupCanvas.width = 1;
  lookupCanvas.height = 256;

  const lookupCtx = lookupCanvas.getContext("2d");
  if (!lookupCtx) return null;

  const gradient = lookupCtx.createLinearGradient(0, 255, 0, 0);
  const lastIndex = HEATMAP_GRADIENT.length - 1;

  HEATMAP_GRADIENT.forEach((color, index) => {
    gradient.addColorStop(index / lastIndex, color);
  });

  lookupCtx.fillStyle = gradient;
  lookupCtx.fillRect(0, 0, 1, 256);

  gradientLookupCache = lookupCtx.getImageData(0, 0, 1, 256).data;
  return gradientLookupCache;
};

const getRenderCache = ({ canvas, ctx, renderCache }) => {
  const width = Math.max(1, Math.ceil(canvas.width * HEATMAP_RENDER_SCALE));
  const height = Math.max(1, Math.ceil(canvas.height * HEATMAP_RENDER_SCALE));

  if (
    renderCache.densityCanvas &&
    renderCache.densityCanvas.width === width &&
    renderCache.densityCanvas.height === height &&
    renderCache.outputImage
  ) {
    renderCache.outputImage.data.fill(0);
    return renderCache;
  }

  const densityCanvas = document.createElement("canvas");
  densityCanvas.width = width;
  densityCanvas.height = height;

  renderCache.densityCanvas = densityCanvas;
  renderCache.densityCtx = densityCanvas.getContext("2d");
  renderCache.outputImage = ctx.createImageData(width, height);

  return renderCache;
};

const drawCanvasHeatmap = ({
  canvas,
  projection,
  points,
  maxIntensity,
  renderCache,
}) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const cssWidth = Number(canvas.dataset.cssWidth || 0);
  const cssHeight = Number(canvas.dataset.cssHeight || 0);
  const renderWidth = Math.max(1, Math.ceil(canvas.width * HEATMAP_RENDER_SCALE));
  const renderHeight = Math.max(1, Math.ceil(canvas.height * HEATMAP_RENDER_SCALE));
  const scaleX = renderWidth / Math.max(1, cssWidth);
  const scaleY = renderHeight / Math.max(1, cssHeight);

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!points.length || !maxIntensity) return;

  const left = Number(canvas.dataset.left || 0);
  const top = Number(canvas.dataset.top || 0);
  const cache = getRenderCache({ canvas, ctx, renderCache });
  const densityCtx = cache.densityCtx;
  if (!densityCtx || !cache.outputImage) return;

  densityCtx.setTransform(1, 0, 0, 1, 0, 0);
  densityCtx.clearRect(0, 0, renderWidth, renderHeight);
  densityCtx.globalCompositeOperation = "lighter";
  densityCtx.filter = `blur(${Math.max(1, Math.round(8 * HEATMAP_RENDER_SCALE))}px)`;

  points.forEach((point) => {
    const pixel = projection.fromLatLngToDivPixel(
      new window.google.maps.LatLng(point.lat, point.lng)
    );
    if (!pixel) return;

    const x = (pixel.x - left) * scaleX;
    const y = (pixel.y - top) * scaleY;
    const radius = HEATMAP_RADIUS_PX * ((scaleX + scaleY) / 2);
    const normalizedIntensity = Math.max(
      0.08,
      Math.min(1, Number(point.intensity || 1) / maxIntensity)
    );
    const gradient = densityCtx.createRadialGradient(
      x,
      y,
      0,
      x,
      y,
      radius
    );

    gradient.addColorStop(0, `rgba(0, 0, 0, ${0.85 * normalizedIntensity})`);
    gradient.addColorStop(0.5, `rgba(0, 0, 0, ${0.34 * normalizedIntensity})`);
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    densityCtx.fillStyle = gradient;
    densityCtx.fillRect(
      x - radius,
      y - radius,
      radius * 2,
      radius * 2
    );
  });

  densityCtx.filter = "none";

  const densityImage = densityCtx.getImageData(0, 0, renderWidth, renderHeight);
  const gradientLookup = createGradientLookup();
  if (!gradientLookup) return;
  const outputImage = cache.outputImage;

  for (let i = 0; i < densityImage.data.length; i += 4) {
    const alpha = densityImage.data[i + 3];
    if (alpha < 8) continue;

    const lookupIndex = Math.min(255, alpha) * 4;
    outputImage.data[i] = gradientLookup[lookupIndex];
    outputImage.data[i + 1] = gradientLookup[lookupIndex + 1];
    outputImage.data[i + 2] = gradientLookup[lookupIndex + 2];
    outputImage.data[i + 3] = Math.min(235, alpha);
  }

  cache.outputCanvas ||= document.createElement("canvas");
  cache.outputCanvas.width = renderWidth;
  cache.outputCanvas.height = renderHeight;
  cache.outputCtx ||= cache.outputCanvas.getContext("2d");
  if (!cache.outputCtx) return;

  cache.outputCtx.putImageData(outputImage, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(cache.outputCanvas, 0, 0, canvas.width, canvas.height);
};

const AnalyticsHeatmapLayer = ({ map, points, maxIntensity }) => {
  const overlayRef = useRef(null);
  const canvasRef = useRef(null);
  const pointsRef = useRef(points);
  const maxIntensityRef = useRef(maxIntensity);
  const renderCacheRef = useRef({});
  const drawTimerRef = useRef(null);

  const scheduleDraw = () => {
    if (!overlayRef.current) return;
    if (drawTimerRef.current) window.clearTimeout(drawTimerRef.current);

    drawTimerRef.current = window.setTimeout(() => {
      drawTimerRef.current = null;
      overlayRef.current?.draw?.();
    }, DRAW_DEBOUNCE_MS);
  };

  useEffect(() => {
    pointsRef.current = points;
    maxIntensityRef.current = maxIntensity;
    scheduleDraw();
  }, [points, maxIntensity]);

  useEffect(() => {
    if (!map || typeof window === "undefined" || !window.google?.maps?.OverlayView) {
      return undefined;
    }

    const overlay = new window.google.maps.OverlayView();

    overlay.onAdd = function onAdd() {
      const canvas = document.createElement("canvas");
      canvas.style.position = "absolute";
      canvas.style.pointerEvents = "none";
      canvasRef.current = canvas;
      this.getPanes().overlayLayer.appendChild(canvas);
    };

    overlay.draw = function draw() {
      const canvas = canvasRef.current;
      const projection = this.getProjection();
      const mapDiv = map.getDiv();
      const center = map.getCenter();

      if (!canvas || !projection || !mapDiv || !center) return;

      const centerPixel = projection.fromLatLngToDivPixel(center);
      if (!centerPixel) return;

      const width = Math.max(1, mapDiv.clientWidth || 0);
      const height = Math.max(1, mapDiv.clientHeight || 0);
      const left = centerPixel.x - width / 2;
      const top = centerPixel.y - height / 2;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

      canvas.style.left = `${left}px`;
      canvas.style.top = `${top}px`;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const nextCanvasWidth = Math.ceil(width * dpr);
      const nextCanvasHeight = Math.ceil(height * dpr);
      if (canvas.width !== nextCanvasWidth) canvas.width = nextCanvasWidth;
      if (canvas.height !== nextCanvasHeight) canvas.height = nextCanvasHeight;
      canvas.dataset.left = String(left);
      canvas.dataset.top = String(top);
      canvas.dataset.cssWidth = String(width);
      canvas.dataset.cssHeight = String(height);
      canvas.style.opacity = "0";

      if (drawTimerRef.current) window.clearTimeout(drawTimerRef.current);
      drawTimerRef.current = window.setTimeout(() => {
        drawTimerRef.current = null;
        drawCanvasHeatmap({
          canvas,
          projection,
          points: pointsRef.current,
          maxIntensity: maxIntensityRef.current,
          renderCache: renderCacheRef.current,
        });
        canvas.style.opacity = "1";
      }, DRAW_DEBOUNCE_MS);
    };

    overlay.onRemove = function onRemove() {
      canvasRef.current?.remove();
      canvasRef.current = null;
      renderCacheRef.current = {};
    };

    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      if (drawTimerRef.current) {
        window.clearTimeout(drawTimerRef.current);
        drawTimerRef.current = null;
      }
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map]);

  return null;
};

export default AnalyticsHeatmapLayer;
